import { atom } from "jotai";
import { Node } from "@xyflow/react";
import {
  closeContextMenusAtom,
  floatingToolbarActiveButtonAtom,
  selectedNodeTypeAtom,
} from "../diagramming";
import { nodesAtom } from "./nodesAtom";
import {
  getNodePositionInsideParent,
  getEventRawText,
  snapToAdjacentNode,
} from "../utils";
import { DRAG_AND_DROP } from "@zapier/canvas-constants";
import { addAndSavePathNodeSetter } from "../paths";
import { saveCanvasSetter } from "../persistence";
import { addNodeSetter } from "./addNodeSetter";
import { PathNodeData } from "../types";
import { isEditingTextNodeAtomFamily } from "./isEditingTextNodeAtomFamily";
import { getNodeDataAtom } from "./getNodeDataAtom";

type Params = {
  event: React.MouseEvent;
  /** parentId is when you are trying to drop a node on a group node
   */
  parentId?: string;
  ref: React.MutableRefObject<HTMLDivElement | null>;
  emitEventOnAddStep: (interactionType: string, rawText: string) => void;
  emitEventOnAddPath: (interactionType: string, rawText: string) => void;
  screenToFlowPosition: (point: { x: number; y: number }) => {
    x: number;
    y: number;
  };
  getIntersectingNodes: (rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => Node[];
};

export const addNodeOnClickSetter = atom(
  null,
  async (
    get,
    set,
    {
      event,
      parentId,
      ref,
      emitEventOnAddStep,
      emitEventOnAddPath,
      screenToFlowPosition,
      getIntersectingNodes,
    }: Params,
  ) => {
    // Close the context menus if they're open whenever the window is clicked.
    set(closeContextMenusAtom);
    const nodes = get(nodesAtom);
    const selectedNodeType = get(selectedNodeTypeAtom);

    if (selectedNodeType === "group") {
      // You can only create a group node by clicking and dragging. If you
      // choose the group node option and click, we don't want to create a
      // 1x1 group node because it will be very hard to see and users might
      // not understand what just happened
      return;
    }

    const reactFlowBounds = ref?.current?.getBoundingClientRect();

    if (reactFlowBounds) {
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      if (selectedNodeType !== null) {
        const intersections = getIntersectingNodes({
          x: position.x,
          y: position.y,
          width: 40,
          height: 40,
        }).filter((n) => n.type === "group");

        const groupNode = intersections[0];

        let newNode = get(getNodeDataAtom)({
          type: selectedNodeType,
          position,
        }) as Node;

        if (newNode === null) {
          set(selectedNodeTypeAtom, null);
          return;
        }

        if (parentId) {
          newNode.parentId = parentId;
        }

        if (groupNode) {
          // if we drop a node on a group node, we want to position the node inside the group
          newNode.position = getNodePositionInsideParent(
            {
              position,
              width: 40,
              height: 40,
            },
            groupNode,
          ) ?? { x: 0, y: 0 };
          newNode.parentId = groupNode?.id;
        }

        const rawText = getEventRawText(selectedNodeType);

        const isPathNode = selectedNodeType === "path";
        const isZapNode = selectedNodeType === "zap";
        const isTextNode = selectedNodeType === "text";

        // newNode will have updated position after running snapToAdjacentNode
        newNode = snapToAdjacentNode({ newNode, nodes, isPathNode });

        if (isPathNode) {
          void set(addAndSavePathNodeSetter, {
            sourceNodeId: null,
            nodePartial: newNode as Partial<Node<PathNodeData>>,
            numOfPaths: 2,
            shouldAddIncomerToPathNodeEdge: false, // since we already add this above when swapping temp edge for real one
          });

          emitEventOnAddPath(DRAG_AND_DROP, rawText);
        } else {
          // This is so the FloatingZapsTypeahead shows up automatically when using the diagramming toolbar
          if (isZapNode && !newNode.data.zapId) {
            setTimeout(() => set(floatingToolbarActiveButtonAtom, "link"), 0);
          }

          if (isTextNode) {
            /* setTimeout in order to focus the editor and show text cursor flashing
            so user can start typing right away
            * */
            setTimeout(
              () => set(isEditingTextNodeAtomFamily(newNode.id), true),
              0,
            );
          }

          // we need to make sure that the parents are sorted before the children
          // to make sure that the children are rendered on top of the parents
          const updatedNodes = set(addNodeSetter, newNode);

          void set(saveCanvasSetter, {
            nodes: updatedNodes,
          });

          emitEventOnAddStep(DRAG_AND_DROP, rawText);
        }

        set(selectedNodeTypeAtom, null);
      }
    }
  },
);

addNodeOnClickSetter.debugLabel = "addNodeOnClickSetter";
